import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface FormaPagamentoType {
    id: number;
    name: string;
    linkGateway?: string;
    token?: string;
}

export interface CreateFormaPagamentoType {
    name: string
    linkGateway?: string;
    token?: string;
    empresaId: number;
}

export interface UpdateFormaPagamentoType {
    id: number;
    name: string;
    linkGateway?: string;
    token?: string;
}

export default function useFormaPagamento() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listFormaPagamento, setListFormaPagamento] = useState<FormaPagamentoType[]>([]);
    const [listFormaPagamentoByEmpresa, setListFormaPagamentoByEmpresa] = useState<FormaPagamentoType[]>([]);
    const [formaPagamentoPageCount, setFormaPagamentoPageCount] = useState<number>(0);
    const [initialListFormaPagamento, setInitialListFormaPagamento] = useState<FormaPagamentoType[]>([]);
    const [formaPagamento, setFormaPagamento] = useState<FormaPagamentoType>({
        id: 0,
        name: "",
        linkGateway: "",
        token: "",
    });

    function createFormaPagamento(createFormaPagamentoType: CreateFormaPagamentoType): Promise<CreateFormaPagamentoType | null> {
        setLoading(true);
        return api
            .post(`/FormaPagamento/create`, createFormaPagamentoType)
            .then((response) => {
                const createdFormaPagamento: CreateFormaPagamentoType = response.data;
                showToast("success", "Forma de pagamento criada com sucesso.");
                setLoading(false);
                return createdFormaPagamento;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar forma de pagamento. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar forma de pagamento:", error);
                return null;
            });
    }

    function getAllFormaPagamentoPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<FormaPagamentoType[] | null> {
        setLoading(true);
        let url = `/FormaPagamento/getAllFormaPagamentoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListFormaPagamento(response.data.result);
                setInitialListFormaPagamento(response.data.result);
                setFormaPagamentoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar formas de pagamento. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar formas de pagamento:", error);
                return null;
            });
    }

    function updateFormaPagamento(updateFormaPagamentoData: UpdateFormaPagamentoType): Promise<UpdateFormaPagamentoType | null> {
        setLoading(true);
        return api
            .put(`/FormaPagamento/${updateFormaPagamentoData.id}`, updateFormaPagamentoData)
            .then((response) => {
                showToast("success", "Forma de pagamento atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar forma de pagamento. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar forma de pagamento:", error);
                return null;
            });
    }

    function deleteFormaPagamento(formaPagamentoId: number): Promise<FormaPagamentoType | null> {
        setLoading(true);
        return api
            .delete(`/FormaPagamento/${formaPagamentoId}`)
            .then(() => {
                setFormaPagamento({
                    id: 0,
                    name: "",
                    linkGateway: "",
                    token: "",
                });
                showToast("success", "Forma de pagamento deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar forma de pagamento. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar forma de pagamento:", error);
                return null;
            });
    }

    function handleGetFormaPagamentoByEmpresaId(empresaId: number): Promise<FormaPagamentoType[] | null> {
        setLoading(true);
        return api
            .get(`/FormaPagamento/getFormaPagamentoByEmpresaId/${empresaId}`)
            .then((response) => {
                const formaPagamento: FormaPagamentoType[] = response.data;
                setListFormaPagamentoByEmpresa(formaPagamento);
                setLoading(false);
                return formaPagamento;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das formas de pagamento. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das formas de pagamento:", error);
                return null;
            });
    }

    return {
        createFormaPagamento,
        getAllFormaPagamentoPaged,
        updateFormaPagamento,
        deleteFormaPagamento,
        setListFormaPagamento,
        listFormaPagamento,
        setFormaPagamentoPageCount,
        formaPagamentoPageCount,
        setInitialListFormaPagamento,
        initialListFormaPagamento,
        setFormaPagamento,
        formaPagamento,
        handleGetFormaPagamentoByEmpresaId,
        setListFormaPagamentoByEmpresa,
        listFormaPagamentoByEmpresa,
    };
}